import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2, CalendarClock, AlertOctagon, ReceiptText, Landmark, BellRing, Plus, Upload, FileText, CalendarDays, Loader2,
  ShieldCheck, LifeBuoy, AlertTriangle, ArrowUpRight, Database,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { HealthGauge } from "@/components/app/HealthGauge";
import { PageState, StatusPill, Empty } from "@/components/app/Common";
import { DocViewer } from "@/components/app/DocViewer";
import { useLoad } from "@/hooks/useLoad";
import { api, errMsg } from "@/lib/api";
import { bandColor, bandFor, daysLabel, fmtDate } from "@/lib/ui";
import { useAuth, isRestricted } from "@/context/AuthContext";
import { AttentionBoard } from "@/components/app/AttentionBoard";

const SUBS = [["compliance", "Compliance", "35%"], ["tax", "Tax", "25%"], ["governance", "Governance", "25%"], ["receipts", "Receipts", "15%"]];

const ScoreCard = ({ health }) => (
  <div className="surface p-6 sm:p-8 lg:col-span-5 relative overflow-hidden" data-testid="health-score-card">
    <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full blur-3xl opacity-20" style={{ background: bandColor(health.band) }} />
    <div className="eyebrow">Group Corporate Health Score</div>
    <div className="flex flex-col sm:flex-row items-center gap-8 mt-4">
      <HealthGauge score={health.score} band={health.band} size={220} />
      <div className="flex-1 w-full space-y-4">
        {SUBS.map(([k, l, w]) => {
          const v = health.subscores[k];
          return (
            <div key={k} data-testid={`subscore-${k}`}>
              <div className="flex justify-between text-xs mb-1.5"><span className="text-muted-foreground">{l} <span className="opacity-60">· {w}</span></span><span className="font-mono font-semibold">{v}</span></div>
              <div className="h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${v}%`, background: bandColor(bandFor(v)) }} /></div>
            </div>
          );
        })}
        <div className="text-xs text-muted-foreground pt-1">Portfolio risk: <span className="font-semibold text-foreground" data-testid="portfolio-risk">{health.portfolio_risk}</span> · {health.entities_at_risk} entit{health.entities_at_risk === 1 ? "y" : "ies"} below 50</div>
        <div className="text-[11px] text-muted-foreground" data-testid="score-explainer">Group score blends the average entity score (70%) with your weakest entity (30%), so one entity at risk is never hidden.</div>
      </div>
    </div>
  </div>
);

const Diagnosis = ({ d }) => {
  const k = d.kpis;
  const tiles = [
    ["Compliance status", k.overdue_filings ? `${k.overdue_filings} filing(s) overdue` : "All filings current", ShieldCheck, k.overdue_filings ? "Overdue" : "Compliant", "/app/compliance"],
    ["Tax status", k.overdue_taxes ? `${k.overdue_taxes} tax item(s) overdue` : `${k.tax_deadlines} deadline(s) in 60 days`, Landmark, k.overdue_taxes ? "Overdue" : "Upcoming", "/app/tax"],
    ["Missing filings", d.next_filings.filter((f) => ["Overdue", "Critical"].includes(f.status)).map((f) => f.title).slice(0, 1)[0] || "None missing", AlertTriangle, k.overdue_filings ? "Critical" : "Compliant", "/app/compliance"],
    ["Dissolution risk", k.dissolved_entities ? `${k.dissolved_entities} entity dissolved` : d.health.entities_at_risk ? `${d.health.entities_at_risk} at high risk` : "No entity at risk", AlertOctagon, k.dissolved_entities ? "Dissolved" : d.health.entities_at_risk ? "Due Soon" : "Compliant", "/app/entities"],
    ["Revival requirements", k.dissolved_entities ? `Revival needed: ${d.dissolved.map((x) => x.name).join(", ")}` : "No revivals required", LifeBuoy, k.dissolved_entities ? "Critical" : "Compliant", "/app/revival"],
    ["Uncategorized receipts", k.missing_receipts ? `${k.missing_receipts} awaiting review` : "All receipts posted", ReceiptText, k.missing_receipts ? "Due Soon" : "Compliant", "/app/receipts"],
  ];
  return (
    <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4" data-testid="instant-diagnosis">
      {tiles.map(([t, text, Icon, st, to], i) => (
        <Link key={t} to={to} className="surface p-5 lift rise group" style={{ animationDelay: `${i * 60}ms` }} data-testid={`diagnosis-${t.toLowerCase().replace(/\s+/g, "-")}`}>
          <div className="flex items-center justify-between">
            <Icon className="h-4 w-4 text-muted-foreground" />
            <StatusPill status={st} />
          </div>
          <div className="mt-3 text-sm font-semibold flex items-center gap-1">{t}<ArrowUpRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-60 transition-opacity" /></div>
          <div className="text-sm text-muted-foreground mt-0.5 line-clamp-2">{text}</div>
        </Link>
      ))}
    </div>
  );
};

const KPIS = [
  ["total_entities", "Total Entities", Building2], ["upcoming_filings", "Upcoming Filings", CalendarClock], ["overdue_filings", "Overdue Filings", AlertOctagon],
  ["missing_receipts", "Missing Receipts", ReceiptText], ["tax_deadlines", "Tax Deadlines", Landmark], ["compliance_alerts", "Compliance Alerts", BellRing],
];

const KpiRow = ({ k }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 my-6">
    {KPIS.map(([key, label, Icon]) => {
      const warn = ["overdue_filings", "compliance_alerts"].includes(key) && k[key] > 0;
      return (
        <div key={key} className="surface p-5" data-testid={`kpi-${key}`}>
          <div className="flex items-center justify-between"><span className="text-xs text-muted-foreground">{label}</span><Icon className={`h-4 w-4 ${warn ? "text-orange-500" : "text-muted-foreground"}`} /></div>
          <div className={`font-mono text-3xl font-semibold mt-3 ${warn ? "text-orange-500" : ""}`} data-testid={`kpi-${key}-value`}>{k[key]}</div>
        </div>
      );
    })}
  </div>
);

const Factors = ({ d }) => (
  <div className="surface p-6 lg:col-span-7 min-w-0 overflow-hidden" data-testid="score-factors">
    <div className="flex flex-wrap gap-2 items-center justify-between mb-4"><h3 className="font-semibold">Why your score is moving</h3><span className="text-xs text-muted-foreground">Largest impacts first</span></div>
    {d.health.factors.length === 0 ? <p className="text-sm text-muted-foreground">No negative factors. Excellent work.</p> : (
      <ul className="divide-y divide-border">
        {d.health.factors.slice(0, 7).map((f, i) => (
          <li key={i} className="py-2.5 flex items-center gap-3 text-sm">
            <span className="font-mono text-xs w-10 text-right text-red-500">{f.impact}</span>
            <span className="hidden sm:inline text-[11px] rounded border border-border px-1.5 py-0.5 text-muted-foreground w-24 text-center shrink-0">{f.area}</span>
            <span className="flex-1 min-w-0">{f.text}</span>
            <span className="text-xs text-muted-foreground hidden sm:block truncate max-w-[180px]">{f.entity}</span>
          </li>
        ))}
      </ul>
    )}
    <h3 className="font-semibold mt-6 mb-3">Next filings</h3>
    <ul className="space-y-2">
      {d.next_filings.map((f) => (
        <li key={f.id} className="flex items-center gap-3 text-sm" data-testid={`next-filing-${f.id}`}>
          <StatusPill status={f.status} /><span className="flex-1 truncate min-w-0">{f.title} · <span className="text-muted-foreground">{f.entity_name}</span></span>
          <span className="font-mono text-xs text-muted-foreground hidden sm:inline">{fmtDate(f.due_date)} · {daysLabel(f.days_until)}</span>
        </li>
      ))}
    </ul>
  </div>
);

const SidePanel = ({ d, onReport, reporting }) => {
  const nav = useNavigate();
  const { user } = useAuth();
  const actions = [
    ...(isRestricted(user) ? [] : [["Add Corporation", Plus, () => nav("/app/entities?new=1"), "qa-add-corporation"]]),
    ["Upload Receipts", Upload, () => nav("/app/receipts"), "qa-upload-receipts"],
    ["Generate Compliance Report", reporting ? Loader2 : FileText, onReport, "qa-generate-report"],
    ["Review Deadlines", CalendarDays, () => nav("/app/calendar?view=agenda"), "qa-review-deadlines"],
  ];
  return (
    <div className="lg:col-span-5 space-y-6">
      <div className="surface p-6">
        <h3 className="font-semibold mb-4">Quick actions</h3>
        <div className="grid grid-cols-2 gap-3">
          {actions.map(([l, Icon, fn, id]) => (
            <button key={l} onClick={fn} disabled={id === "qa-generate-report" && reporting} data-testid={id}
              className="lift rounded-lg border border-border p-4 text-left hover:bg-accent">
              <Icon className={`h-4 w-4 text-primary ${Icon === Loader2 ? "animate-spin" : ""}`} />
              <div className="text-sm font-medium mt-2">{l}</div>
            </button>
          ))}
        </div>
      </div>
      <div className="surface p-6" data-testid="activity-feed">
        <h3 className="font-semibold mb-4">Recent activity</h3>
        {d.activity.length === 0 ? <p className="text-sm text-muted-foreground">No activity yet.</p> : (
          <ol className="relative border-l border-border ml-1.5 space-y-4">
            {d.activity.slice(0, 8).map((a) => (
              <li key={a.id} className="pl-4 relative">
                <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full bg-primary/70 ring-4 ring-background" />
                <div className="text-sm">{a.message}</div>
                <div className="text-xs text-muted-foreground">{new Date(a.created_at).toLocaleString()}</div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
};

const Onboarding = ({ onSeed, seeding }) => {
  const nav = useNavigate();
  return (
    <Empty icon={Building2} title="Add your first corporation" testId="dashboard-empty"
      text="Your Corporate Health Score appears once an entity is added. Start with your own corporation or explore a ready-made holding company portfolio.">
      <Button onClick={() => nav("/app/entities?new=1")} data-testid="empty-add-corporation-btn"><Plus className="h-4 w-4 mr-1" />Add corporation</Button>
      <Button variant="outline" onClick={onSeed} disabled={seeding} data-testid="load-demo-btn">
        {seeding ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Database className="h-4 w-4 mr-1" />}Load demo portfolio
      </Button>
    </Empty>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const state = useLoad(() => api.get("/dashboard").then((r) => r.data));
  const [doc, setDoc] = useState(null);
  const [reporting, setReporting] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const seed = async () => {
    setSeeding(true);
    try {
      await api.post("/demo/seed");
      toast.success("Demo portfolio loaded");
      state.reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSeeding(false);
    }
  };
  const report = async () => {
    setReporting(true);
    try {
      setDoc((await api.post("/ai/generate", { kind: "compliance_report" })).data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setReporting(false);
    }
  };
  const hour = new Date().getHours();
  return (
    <PageState state={state}>
      {state.data && (
        <div>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8 rise">
            <div>
              <div className="eyebrow mb-2">Executive overview</div>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="dashboard-greeting">
                Good {hour < 12 ? "morning" : hour < 18 ? "afternoon" : "evening"}, {(user?.name || "").split(" ")[0]}
              </h1>
            </div>
            {state.data.health.has_entities && user?.workspace?.role === "owner" && (
              <Button variant="ghost" size="sm" className="text-muted-foreground" data-testid="clear-demo-btn"
                onClick={async () => { await api.delete("/demo"); state.reload(); }}>Clear demo data</Button>
            )}
          </div>
          {!state.data.health.has_entities ? <Onboarding onSeed={seed} seeding={seeding} /> : (
            <>
              <AttentionBoard />
              <div className="grid lg:grid-cols-12 gap-6"><ScoreCard health={state.data.health} /><Diagnosis d={state.data} /></div>
              <KpiRow k={state.data.kpis} />
              <div className="grid lg:grid-cols-12 gap-6"><Factors d={state.data} /><SidePanel d={state.data} onReport={report} reporting={reporting} /></div>
            </>
          )}
          <DocViewer doc={doc} onClose={() => setDoc(null)} />
        </div>
      )}
    </PageState>
  );
}
